/**
 * Asignación de recursos "en bolsa" a residentes/vehículos (F10).
 *
 * La casa puede tener llaveros y controles remotos entregados pero **sin
 * asignar** (en la "bolsa"). El vecino los reparte: llaveros → residente,
 * controles → residente + vehículo. Es asignación DIRECTA (ya están
 * aprobados; no pasa por validación de la Comisión — D-P2-1).
 *
 * Antes esto vivía solo en el wizard de onboarding; se extrajo acá para
 * reusarlo también en "Mis Datos" (un vecino que ya pasó el onboarding
 * igual necesita asignar lo que quede en la bolsa).
 */
import { useState } from 'react';
import { ApiError } from '../../lib/api/client';
import { asignarRecursoMe } from '../../lib/api/recursos';
import type {
  RecursoConSuspensionRead,
  VehiculoRead,
} from '../../lib/api/types';
import type { ResidenteConPersonaRead } from '../../lib/api/residentes';
import {
  residentesConLlaveroVivo,
  vehiculosConCrVivo,
} from '../../lib/recursos';
import { Button } from '../ui/Button';

const ESTILO_INPUT =
  'rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 text-sm';

/** Llaveros entregados a la casa pero sin asignar a un residente (bolsa).
 *  Definición única de "llavero en bolsa" — consumida por wizard y Mis Datos. */
export function llaverosEnBolsa(
  recursos: RecursoConSuspensionRead[],
): RecursoConSuspensionRead[] {
  return recursos.filter(
    (r) =>
      r.tipo === 'llavero' &&
      r.estado === 'entregado' &&
      r.residente_actual_id === null,
  );
}

/** Controles remotos entregados a la casa pero sin asignar a un vehículo. */
export function controlesEnBolsa(
  recursos: RecursoConSuspensionRead[],
): RecursoConSuspensionRead[] {
  return recursos.filter(
    (r) =>
      r.tipo === 'control_remoto' &&
      r.estado === 'entregado' &&
      r.vehiculo_id === null,
  );
}

function extraerError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return typeof err.body === 'object' &&
      err.body !== null &&
      'detail' in err.body
      ? String((err.body as { detail: unknown }).detail)
      : `Error ${err.status}`;
  }
  return fallback;
}

/**
 * Fila de asignación de UN recurso de la bolsa.
 *
 * Sprint 22: la fila pasó de tener estado propio a ser **controlada** por
 * `SeccionAsignarRecursos`. Antes cada fila guardaba su selección en un
 * `useState` local, así que no sabía qué habían elegido las filas hermanas y
 * el vecino podía poner a la misma persona en los tres llaveros. Ahora la
 * selección vive arriba y el padre le pasa la lista de candidatos ya
 * descontada: quien fue elegido en otra fila desaparece de esta al instante,
 * sin necesidad de grabar.
 */
export function FilaAsignarRecurso({
  recurso,
  residentes,
  requiereVehiculo,
  vehiculos,
  residenteId,
  vehiculoId,
  onCambiarResidente,
  onCambiarVehiculo,
  onAsignado,
}: {
  recurso: RecursoConSuspensionRead;
  /** Candidatos disponibles para ESTA fila (ya descontados los ocupados). */
  residentes: ResidenteConPersonaRead[];
  requiereVehiculo: boolean;
  /** Vehículos disponibles para ESTA fila (ya descontados los ocupados). */
  vehiculos: VehiculoRead[];
  residenteId: string;
  vehiculoId: string;
  onCambiarResidente: (residenteId: string) => void;
  onCambiarVehiculo: (vehiculoId: string) => void;
  onAsignado: () => void;
}) {
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valido = residenteId !== '' && (!requiereVehiculo || vehiculoId !== '');

  async function asignar() {
    if (!valido || guardando) return;
    setGuardando(true);
    setError(null);
    try {
      await asignarRecursoMe(recurso.id, {
        residente_id: residenteId,
        vehiculo_id: requiereVehiculo ? vehiculoId : null,
      });
      onAsignado();
    } catch (err) {
      setError(extraerError(err, 'No se pudo asignar.'));
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-text-primary font-mono">
          {recurso.identificador}
          {recurso.serial_rfid ? ` - ${recurso.serial_rfid}` : ''}
        </span>
        {requiereVehiculo && (
          <select
            value={vehiculoId}
            onChange={(e) => onCambiarVehiculo(e.target.value)}
            aria-label="Vehículo"
            className={`${ESTILO_INPUT} flex-1 min-w-[8rem]`}
          >
            <option value="">Vehículo…</option>
            {vehiculos.map((v) => (
              <option key={v.id} value={v.id}>
                {v.patente}
              </option>
            ))}
          </select>
        )}
        <select
          value={residenteId}
          onChange={(e) => onCambiarResidente(e.target.value)}
          aria-label="Residente"
          className={`${ESTILO_INPUT} flex-1 min-w-[8rem]`}
        >
          <option value="">Residente…</option>
          {residentes.map((r) => (
            <option key={r.id} value={r.id}>
              {r.persona_nombre_completo}
            </option>
          ))}
        </select>
        <Button
          variant="secondary"
          onClick={asignar}
          disabled={!valido || guardando}
        >
          {guardando ? '…' : 'Asignar'}
        </Button>
      </div>
      {error && (
        <p className="text-xs text-state-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Selección pendiente (aún sin grabar) de cada fila, por id de recurso. */
type Seleccion = Record<string, { residenteId: string; vehiculoId: string }>;

const SIN_SELECCION = { residenteId: '', vehiculoId: '' };

/**
 * Sección completa de asignación desde la bolsa: muestra llaveros sin
 * asignar (→ residente) y controles sin asignar (→ residente + vehículo).
 * Se renderiza solo si hay algo que asignar. Para "Mis Datos" y el wizard.
 *
 * Sprint 22 — dueño del estado de todas las filas, para poder descontar en
 * vivo lo ya elegido. Dos reglas distintas, porque el recurso escaso es
 * distinto en cada caso:
 *
 *   Llavero → la PERSONA es única: se descuenta a quien ya porta un llavero
 *             vivo y a quien fue elegido en otra fila de llavero.
 *   Control → el AUTO es único: se descuentan los autos que ya tienen control
 *             y los elegidos en otra fila de control. La persona NO se
 *             descuenta: alguien con dos autos lleva dos controles.
 */
export function SeccionAsignarRecursos({
  recursos,
  residentes,
  vehiculos,
  onAsignado,
}: {
  recursos: RecursoConSuspensionRead[];
  residentes: ResidenteConPersonaRead[];
  vehiculos: VehiculoRead[];
  onAsignado: () => void;
}) {
  const [seleccion, setSeleccion] = useState<Seleccion>({});

  const llaverosBolsa = llaverosEnBolsa(recursos);
  const controlesBolsa = controlesEnBolsa(recursos);
  const residentesActivos = residentes.filter((r) => r.estado !== 'inactivo');
  const vehiculosActivos = vehiculos.filter((v) => v.estado !== 'inactivo');

  // Ya asignado y grabado en el backend.
  const conLlavero = residentesConLlaveroVivo(recursos);
  const conControl = vehiculosConCrVivo(recursos);

  const idsLlaveros = new Set(llaverosBolsa.map((r) => r.id));

  /** Personas elegidas en OTRA fila de llavero (todavía sin grabar). */
  function personasElegidasEnOtroLlavero(recursoId: string): Set<string> {
    return new Set(
      Object.entries(seleccion)
        .filter(
          ([id, sel]) =>
            id !== recursoId && idsLlaveros.has(id) && sel.residenteId !== '',
        )
        .map(([, sel]) => sel.residenteId),
    );
  }

  /** Autos elegidos en OTRA fila de control (todavía sin grabar). */
  function autosElegidosEnOtroControl(recursoId: string): Set<string> {
    return new Set(
      Object.entries(seleccion)
        .filter(
          ([id, sel]) =>
            id !== recursoId && !idsLlaveros.has(id) && sel.vehiculoId !== '',
        )
        .map(([, sel]) => sel.vehiculoId),
    );
  }

  function cambiar(
    recursoId: string,
    campo: 'residenteId' | 'vehiculoId',
    valor: string,
  ) {
    setSeleccion((previa) => ({
      ...previa,
      [recursoId]: { ...(previa[recursoId] ?? SIN_SELECCION), [campo]: valor },
    }));
  }

  /** Al grabar una fila, su selección deja de ser pendiente: la lista de
   *  recursos que llega por props ya la va a reflejar como asignada. */
  function asignada(recursoId: string) {
    setSeleccion((previa) => {
      const resto = { ...previa };
      delete resto[recursoId];
      return resto;
    });
    onAsignado();
  }

  if (llaverosBolsa.length === 0 && controlesBolsa.length === 0) return null;

  return (
    <div className="space-y-3">
      {llaverosBolsa.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-text-muted">
            Llaveros sin asignar ({llaverosBolsa.length})
          </p>
          {residentesActivos.length === 0 ? (
            <p className="text-xs text-text-muted">
              Agrega un residente para poder asignarle un llavero.
            </p>
          ) : (
            llaverosBolsa.map((ll) => {
              const ocupadas = personasElegidasEnOtroLlavero(ll.id);
              const candidatos = residentesActivos.filter(
                (r) => !conLlavero.has(r.id) && !ocupadas.has(r.id),
              );
              const sel = seleccion[ll.id] ?? SIN_SELECCION;
              return (
                <div key={ll.id} className="space-y-1">
                  <FilaAsignarRecurso
                    recurso={ll}
                    residentes={candidatos}
                    requiereVehiculo={false}
                    vehiculos={[]}
                    residenteId={sel.residenteId}
                    vehiculoId=""
                    onCambiarResidente={(v) => cambiar(ll.id, 'residenteId', v)}
                    onCambiarVehiculo={() => undefined}
                    onAsignado={() => asignada(ll.id)}
                  />
                  {/* Sin candidatos y sin nada elegido: el selector vacío con el
                      botón apagado no explica nada. Este aviso dice qué falta. */}
                  {candidatos.length === 0 && sel.residenteId === '' && (
                    <p className="text-xs text-text-muted">
                      No queda nadie sin llavero en tu casa. Agrega a la persona
                      que va a usar este llavero en la pestaña "Residentes" y
                      vuelve aquí para asignárselo.
                    </p>
                  )}
                </div>
              );
            })
          )}
          {residentesActivos.length > 0 &&
            residentesActivos.every((r) => conLlavero.has(r.id)) && (
              <p className="text-xs text-text-muted">
                Todas las personas registradas en tu casa ya tienen su llavero.
                Cada persona lleva uno: agrega a quien falte para poder
                asignarle el que queda.
              </p>
            )}
        </div>
      )}
      {controlesBolsa.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-text-muted">
            Controles remotos sin asignar ({controlesBolsa.length})
          </p>
          {vehiculosActivos.length === 0 ? (
            <p className="text-xs text-text-muted">
              Agrega un vehículo para poder asignarle un control remoto.
            </p>
          ) : (
            controlesBolsa.map((cr) => {
              const ocupados = autosElegidosEnOtroControl(cr.id);
              const autos = vehiculosActivos.filter(
                (v) => !conControl.has(v.id) && !ocupados.has(v.id),
              );
              const sel = seleccion[cr.id] ?? SIN_SELECCION;
              return (
                <div key={cr.id} className="space-y-1">
                  <FilaAsignarRecurso
                    recurso={cr}
                    residentes={residentesActivos}
                    requiereVehiculo
                    vehiculos={autos}
                    residenteId={sel.residenteId}
                    vehiculoId={sel.vehiculoId}
                    onCambiarResidente={(v) => cambiar(cr.id, 'residenteId', v)}
                    onCambiarVehiculo={(v) => cambiar(cr.id, 'vehiculoId', v)}
                    onAsignado={() => asignada(cr.id)}
                  />
                  {autos.length === 0 && sel.vehiculoId === '' && (
                    <p className="text-xs text-text-muted">
                      Todos tus autos ya tienen control remoto. Agrega el
                      vehículo que va a usar este control en la pestaña
                      "Vehículos" y vuelve aquí para asignárselo.
                    </p>
                  )}
                </div>
              );
            })
          )}
          {vehiculosActivos.length > 0 &&
            vehiculosActivos.every((v) => conControl.has(v.id)) && (
              <p className="text-xs text-text-muted">
                Todos tus vehículos ya tienen su control remoto. Cada auto
                lleva uno: agrega el auto que falte para asignarle el que
                queda.
              </p>
            )}
        </div>
      )}
    </div>
  );
}
